/**
 * Trang đăng nhập — trả nợ kỹ thuật D7 của đợt 0; giao diện theo mẫu LoginCard
 * ("Thông tin nguồn"): hộp 450px giữa trang, tiêu đề viết hoa màu `brand`, nút
 * Đăng nhập rộng hết hộp. Màu, logo và nền đồng bộ với trang Giới thiệu
 * (logo chép từ apps/trang-gioi-thieu/src/anh/logo.webp).
 *
 * Hợp đồng A0 đa tỉnh mục 4: tên đăng nhập duy nhất trên toàn hệ thống, máy chủ tự
 * xác định tỉnh theo tài khoản — người dùng chỉ nhập tên trơn, không chọn tỉnh, không
 * gợi ý dạng kèm mã tỉnh. Lỗi máy chủ (nếu có) vẫn hiển thị nguyên văn.
 *
 * Luồng nhiều bước (hợp đồng A0 tài khoản xã mục 3):
 *   1. nhập tên + mật khẩu → POST /auth/login;
 *   2. nếu `canDoiMatKhau` → màn "Đổi mật khẩu lần đầu" (POST /auth/doi-mat-khau-lan-dau
 *      bằng `phienTam`), phản hồi cùng dạng đăng nhập nên quay lại bước xử lý chung;
 *   3. nếu `canXacThucHaiLop` → nhập mã hai lớp (POST /auth/2fa/xac-thuc);
 *   4. có `accessToken` → vào hệ thống.
 * Phiên tạm chỉ sống trong trạng thái của thành phần, không lưu trình duyệt.
 */
import { useState, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import {
  dangNhap,
  doiMatKhauLanDau,
  xacThucHaiLop,
  type KetQuaDangNhap,
  type PhongBiNoiBo,
} from '../api/khach-api';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { DoManhMatKhau, matKhauDuManh } from '../thanh-phan/DoManhMatKhau';
import anhLogo from '../anh/logo.webp';
import './trang-dang-nhap.css';

const tt = viQuanTri.dangNhapThem;
const tmk = viQuanTri.matKhau;

type Buoc = 'dang-nhap' | 'doi-mat-khau' | 'hai-lop';

export interface TrangDangNhapProps {
  onDangNhapXong: () => void;
}

export function TrangDangNhap({ onDangNhapXong }: TrangDangNhapProps): ReactNode {
  const [ten, datTen] = useState('');
  const [matKhau, datMatKhau] = useState('');
  const [hienMatKhau, datHienMatKhau] = useState(false);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [buoc, datBuoc] = useState<Buoc>('dang-nhap');
  const [phienTam, datPhienTam] = useState<string | null>(null);
  const [matKhauMoi, datMatKhauMoi] = useState('');
  const [nhapLai, datNhapLai] = useState('');
  const [maHaiLop, datMaHaiLop] = useState('');

  /** Về lại bước 1, xoá mọi dữ liệu tạm của các bước sau. */
  const veDangNhap = (thongBao: string | null): void => {
    datBuoc('dang-nhap');
    datPhienTam(null);
    datMatKhauMoi('');
    datNhapLai('');
    datMaHaiLop('');
    datMatKhau('');
    datLoi(thongBao);
  };

  /** Xử lý chung mọi phản hồi cùng dạng đăng nhập: vào hệ thống hoặc chuyển bước. */
  const xuLyKetQua = (pb: PhongBiNoiBo<KetQuaDangNhap>, loiMacDinh: string): void => {
    const d = pb?.data;
    if (!pb?.success || !d) {
      // Phiên tạm hết hạn ở các bước sau → phải đăng nhập lại từ đầu.
      if (buoc !== 'dang-nhap' && pb?.code === 'UNAUTHORIZED') veDangNhap(tt.phienHetHan);
      else datLoi(pb?.message || loiMacDinh);
      return;
    }
    if (d.accessToken) {
      onDangNhapXong();
      return;
    }
    if (d.canDoiMatKhau && d.phienTam) {
      datPhienTam(d.phienTam);
      datBuoc('doi-mat-khau');
      datLoi(null);
      return;
    }
    if (d.canXacThucHaiLop && d.phienTam) {
      datPhienTam(d.phienTam);
      datBuoc('hai-lop');
      datLoi(null);
      return;
    }
    datLoi(pb.message || loiMacDinh);
  };

  const chay = async (
    viec: () => Promise<PhongBiNoiBo<KetQuaDangNhap>>,
    loiMacDinh: string,
  ): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      xuLyKetQua(await viec(), loiMacDinh);
    } catch {
      datLoi(loiMacDinh);
    } finally {
      datDangGui(false);
    }
  };

  const gui = (): Promise<void> => chay(() => dangNhap(ten.trim(), matKhau), vi.dangNhap.loiChung);

  const guiDoiMatKhau = async (): Promise<void> => {
    if (!matKhauDuManh(matKhauMoi)) {
      datLoi(tmk.chuaDuManh);
      return;
    }
    if (matKhauMoi !== nhapLai) {
      datLoi(tmk.khongKhop);
      return;
    }
    if (matKhauMoi === matKhau) {
      datLoi(tmk.trungCu);
      return;
    }
    await chay(() => doiMatKhauLanDau(phienTam ?? '', matKhauMoi), tt.loiDoi);
  };

  const guiHaiLop = (): Promise<void> =>
    chay(() => xacThucHaiLop(phienTam ?? '', maHaiLop.trim()), tt.loiHaiLop);

  const hopLoi = loi ? (
    <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
      {loi}
    </p>
  ) : null;

  if (buoc === 'doi-mat-khau') {
    return (
      <main className="trang-dang-nhap" data-vung="trang-dang-nhap">
        <div className="trang-dang-nhap__nen" aria-hidden="true" />
        <form
          className="trang-dang-nhap__hop"
          data-vung="doi-mat-khau-lan-dau"
          onSubmit={(e) => {
            e.preventDefault();
            void guiDoiMatKhau();
          }}
          noValidate
        >
          <span className="trang-dang-nhap__logo" aria-hidden="true">
            <img src={anhLogo} alt="" width={72} height={72} />
          </span>
          <h1>{tt.doiMatKhauTieuDe}</h1>
          <p className="trang-dang-nhap__phu">{tt.doiMatKhauMoTa}</p>

          <p className="trang-dang-nhap__truong">
            <label htmlFor="dmk-moi">{tt.matKhauMoi}</label>
            <input
              id="dmk-moi"
              type="password"
              autoComplete="new-password"
              value={matKhauMoi}
              onChange={(e) => datMatKhauMoi(e.target.value)}
              aria-describedby="dmk-do-manh"
            />
          </p>
          <DoManhMatKhau matKhau={matKhauMoi} id="dmk-do-manh" />
          <p className="trang-dang-nhap__truong">
            <label htmlFor="dmk-nhap-lai">{tt.nhapLai}</label>
            <input
              id="dmk-nhap-lai"
              type="password"
              autoComplete="new-password"
              value={nhapLai}
              onChange={(e) => datNhapLai(e.target.value)}
            />
          </p>

          {hopLoi}
          <button type="submit" className="trang-dang-nhap__nut" disabled={dangGui}>
            {dangGui ? tt.dangXuLy : tt.nutDoi}
          </button>
          <button type="button" className="nut nut--lien-ket" onClick={() => veDangNhap(null)}>
            {tt.quayLai}
          </button>
        </form>
      </main>
    );
  }

  if (buoc === 'hai-lop') {
    return (
      <main className="trang-dang-nhap" data-vung="trang-dang-nhap">
        <div className="trang-dang-nhap__nen" aria-hidden="true" />
        <form
          className="trang-dang-nhap__hop"
          data-vung="xac-thuc-hai-lop"
          onSubmit={(e) => {
            e.preventDefault();
            void guiHaiLop();
          }}
          noValidate
        >
          <span className="trang-dang-nhap__logo" aria-hidden="true">
            <img src={anhLogo} alt="" width={72} height={72} />
          </span>
          <h1>{tt.haiLopTieuDe}</h1>
          <p className="trang-dang-nhap__phu">{tt.haiLopMoTa}</p>
          <p className="trang-dang-nhap__truong">
            <label htmlFor="hl-ma">{tt.maXacThuc}</label>
            <input
              id="hl-ma"
              autoComplete="one-time-code"
              inputMode="numeric"
              value={maHaiLop}
              onChange={(e) => datMaHaiLop(e.target.value)}
            />
          </p>
          {hopLoi}
          <button type="submit" className="trang-dang-nhap__nut" disabled={dangGui || !maHaiLop.trim()}>
            {dangGui ? tt.dangXuLy : tt.nutXacThuc}
          </button>
          <button type="button" className="nut nut--lien-ket" onClick={() => veDangNhap(null)}>
            {tt.quayLai}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="trang-dang-nhap" data-vung="trang-dang-nhap">
      <div className="trang-dang-nhap__nen" aria-hidden="true" />
      <form
        className="trang-dang-nhap__hop"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
        noValidate
      >
        <span className="trang-dang-nhap__logo" aria-hidden="true">
          <img src={anhLogo} alt="" width={72} height={72} />
        </span>
        <h1>{vi.ungDung.ten}</h1>
        <p className="trang-dang-nhap__phu">{vi.dangNhap.tieuDe}</p>

        <p className="trang-dang-nhap__truong">
          <label htmlFor="dn-ten">{vi.dangNhap.tenDangNhap}</label>
          <input
            id="dn-ten"
            name="tenDangNhap"
            autoComplete="username"
            placeholder={vi.dangNhap.nhapTenDangNhap}
            value={ten}
            onChange={(e) => datTen(e.target.value)}
          />
        </p>

        <p className="trang-dang-nhap__truong">
          <label htmlFor="dn-mat-khau">{vi.dangNhap.matKhau}</label>
          <span className="trang-dang-nhap__mat-khau">
            <input
              id="dn-mat-khau"
              name="matKhau"
              type={hienMatKhau ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={vi.dangNhap.nhapMatKhau}
              value={matKhau}
              onChange={(e) => datMatKhau(e.target.value)}
            />
            <button
              type="button"
              aria-label={hienMatKhau ? vi.dangNhap.anMatKhau : vi.dangNhap.hienMatKhau}
              aria-pressed={hienMatKhau}
              onClick={() => datHienMatKhau((x) => !x)}
            >
              <BieuTuong ten={hienMatKhau ? 'mat-an' : 'mat'} co={18} />
            </button>
          </span>
        </p>

        {hopLoi}
        <button type="submit" className="trang-dang-nhap__nut" disabled={dangGui}>
          {dangGui ? vi.dangNhap.dangXuLy : vi.dangNhap.nut}
        </button>
      </form>
    </main>
  );
}
